import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { 
  Popover, 
  PopoverContent, 
  PopoverTrigger 
} from '@/components/ui/popover';
import { 
  Wand2, 
  CheckCircle, 
  Type, 
  MessageSquare, 
  FileText,
  Loader2,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AI Text Enhancement Component
 * Samsung keyboard-style text enhancement options:
 * - Spelling & Grammar
 * - Professional Tone
 * - Friendly Tone
 * - Summarize
 * - Expand/Elaborate
 * - Simplify
 * 
 * @param {string} text - Current text to enhance
 * @param {function} onTextChange - Callback when text is enhanced
 * @param {string} buttonVariant - Button style variant
 * @param {string} buttonSize - Button size
 * @param {boolean} showLabel - Show "AI Enhance" label
 * @param {boolean} disabled - Disable the button
 * @param {string} context - Context type: general, invoice, message, note
 * @param {string} buttonClassName - Additional CSS classes for button
 */
const AITextEnhancer = ({ 
  text, 
  onTextChange, 
  buttonVariant = "outline",
  buttonSize = "sm",
  showLabel = true,
  disabled = false,
  context = "general",
  buttonClassName = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [activeAction, setActiveAction] = useState(null);

  const enhancementOptions = [
    {
      id: 'grammar',
      label: 'Fix Spelling & Grammar',
      icon: <CheckCircle className="w-4 h-4 text-green-600" />,
      description: 'Correct spelling and grammar errors',
      color: 'hover:bg-green-50'
    },
    {
      id: 'professional',
      label: 'Make Professional',
      icon: <Type className="w-4 h-4 text-blue-600" />,
      description: 'Rewrite in a business tone',
      color: 'hover:bg-blue-50'
    },
    {
      id: 'friendly',
      label: 'Make Friendly',
      icon: <MessageSquare className="w-4 h-4 text-pink-600" />,
      description: 'Rewrite in a warm, friendly tone',
      color: 'hover:bg-pink-50'
    },
    {
      id: 'summarize',
      label: 'Summarize',
      icon: <FileText className="w-4 h-4 text-purple-600" />,
      description: 'Create a shorter version',
      color: 'hover:bg-purple-50'
    },
    {
      id: 'expand',
      label: 'Expand & Elaborate',
      icon: <Sparkles className="w-4 h-4 text-amber-600" />,
      description: 'Add more detail and context',
      color: 'hover:bg-amber-50'
    },
    {
      id: 'simplify',
      label: 'Simplify',
      icon: <Wand2 className="w-4 h-4 text-teal-600" />,
      description: 'Make it easier to understand',
      color: 'hover:bg-teal-50'
    }
  ];

  // Context-specific options
  const getOptionsForContext = () => {
    switch (context) {
      case 'invoice':
        return enhancementOptions.filter(opt => 
          ['grammar', 'professional', 'simplify'].includes(opt.id)
        );
      case 'message':
        return enhancementOptions.filter(opt => 
          ['grammar', 'professional', 'friendly', 'summarize'].includes(opt.id)
        );
      case 'note':
        return enhancementOptions.filter(opt => 
          ['grammar', 'professional', 'expand', 'simplify'].includes(opt.id)
        );
      default:
        return enhancementOptions;
    }
  };

  const handleEnhance = async (option) => {
    if (!text?.trim()) {
      toast.error('Please enter some text first');
      return;
    }

    setLoading(true);
    setActiveAction(option.id);

    try {
      const response = await axios.post(`${API}/ai/enhance-text`, {
        text: text,
        enhancement_type: option.id,
        context: context
      });

      if (response.data.enhanced_text) {
        onTextChange(response.data.enhanced_text);
        toast.success(`Text enhanced: ${option.label.toLowerCase()}`);
        setIsOpen(false);
      }
    } catch (error) {
      console.error('Error enhancing text:', error);
      const errorMsg = error.response?.data?.detail || 'Failed to enhance text. Please try again.';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
      setActiveAction(null);
    }
  };

  const options = getOptionsForContext();

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button 
          variant={buttonVariant} 
          size={buttonSize}
          disabled={disabled || !text?.trim()}
          className={`gap-2 ${buttonClassName}`}
          data-testid="ai-enhance-button"
        >
          <Wand2 className="w-4 h-4" />
          {showLabel && <span>AI Enhance</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="start" data-testid="ai-enhance-popover">
        <div className="space-y-1">
          <div className="flex items-center gap-2 px-2 pb-2 border-b">
            <Sparkles className="w-4 h-4 text-blue-600" />
            <p className="text-sm font-semibold text-gray-700">
              AI Text Enhancement
            </p>
          </div>
          <div className="pt-1">
            {options.map((option) => (
              <button
                key={option.id}
                onClick={() => handleEnhance(option)}
                disabled={loading}
                data-testid={`ai-enhance-${option.id}`}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-xl transition-all text-left disabled:opacity-50 ${option.color}`}
              >
                <div className="flex-shrink-0">
                  {loading && activeAction === option.id ? (
                    <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                  ) : (
                    option.icon
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-gray-900">{option.label}</p>
                  <p className="text-xs text-gray-500 truncate">{option.description}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default AITextEnhancer;
